import { useField } from 'formik';
import React from 'react';
import {
  RichTextEditor as RichTextEditorBase,
  IRichTextEditorProps,
} from '../RichTextEditor';
import { FormField } from '../form';

export interface IFormikRichTextEditorProps
  extends Omit<IRichTextEditorProps, 'value' | 'onChange'> {
  name: string;
  onChange?: (_value: string) => void;
  /** Shows the label above the editor, as every other field in the form does. */
  showLabel?: boolean;
  /** Hint under the editor — what the field is for, or what it accepts. */
  help?: React.ReactNode;
  'data-testid'?: string;
}

/**
 * Formik binding for the rich text editor. Error chrome rides form/FormField
 * (the old version depended on the legacy Input.css .input-container /
 * .input-error globals, which die with the legacy Input); touched is set on
 * blur so errors surface without a submit.
 *
 * The label always names the editor group for assistive tech. `showLabel` also
 * renders it visibly — an unlabelled box next to labelled inputs makes the
 * reader work out what it is from its contents.
 */
export const FormikRichTextEditor: React.FC<IFormikRichTextEditorProps> = ({
  name,
  showLabel = false,
  help,
  'data-testid': testId,
  ...props
}) => {
  const [{ value }, { touched, error }, { setValue, setTouched }] = useField(name);
  const shownError = touched && error ? error : undefined;

  return (
    <div data-testid={testId}>
      <FormField error={shownError} help={help} label={showLabel ? props.label : undefined}>
        <RichTextEditorBase
          {...props}
          value={value}
          onChange={(nextValue) => {
            props.onChange?.(nextValue);
            setValue(nextValue);
          }}
          onBlur={() => setTouched(true)}
          error={shownError}
        />
      </FormField>
    </div>
  );
};
